import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { cn } from '../../lib/utils';
import { HorizontalStack, VerticalStack } from './Stack';
import { DescriptionText } from './Typography';

export interface WorksurfaceHeaderProps {
  /** The name of the parent collection/registry (e.g., "Users") */
  parentLabel: string;
  /** The name of the current active entity (e.g., "John Doe") */
  activeLabel: string;
  /** Optional description text to show below the labels */
  description?: string;
  /** Callback for when the user clicks the back affordance */
  onBack?: () => void;
  /** Optional icon or avatar to display next to the active label */
  icon?: React.ReactNode;
  className?: string;
}

export function WorksurfaceHeader({
  parentLabel,
  activeLabel,
  description,
  onBack,
  icon,
  className,
}: WorksurfaceHeaderProps) {
  return (
    <HorizontalStack gap={2} className={cn('min-w-0 sm:gap-3 3xl:gap-6', className)}>
      {onBack && (
        <button
          onClick={onBack}
          className="group flex h-7 w-7 sm:h-8 sm:w-8 3xl:h-12 3xl:w-12 shrink-0 items-center justify-center rounded-lg border border-border/50 bg-background transition-all hover:bg-muted/50 hover:border-border active:scale-95"
          aria-label={`Back to ${parentLabel}`}
        >
          <ArrowLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4 3xl:h-6 3xl:w-6 text-muted-foreground transition-transform group-hover:-translate-x-0.5" />
        </button>
      )}
      
      <VerticalStack gap={0} className="min-w-0 sm:gap-1 3xl:gap-2">
        <HorizontalStack gap={1} className="min-w-0 sm:gap-2 3xl:gap-4">
          <span className="hidden text-xs font-medium text-muted-foreground/60 sm:inline sm:text-sm 3xl:text-xl">
            {parentLabel}
          </span>
          <span className="hidden text-muted-foreground/30 sm:inline 3xl:text-xl">/</span>
          <div className="flex items-center gap-1.5 min-w-0 sm:gap-2 3xl:gap-4">
            {icon}
            <h1 className="truncate text-lg font-bold tracking-tight text-foreground font-heading leading-none sm:text-xl 3xl:text-4xl">
              {activeLabel}
            </h1>
          </div>
        </HorizontalStack>
        {description && (
          <DescriptionText className="truncate max-w-xl text-[10px] sm:text-xs 3xl:text-lg 3xl:max-w-3xl">
            {description}
          </DescriptionText>
        )}
      </VerticalStack>
    </HorizontalStack>
  );
}
